import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../feature-module/router/all_routes";
import { useChatNotificationContext } from "../../feature-module/application/components/chat/ChatNotificationContext";
import { getChannelIcon, getChannelColor, getChannelColorAlpha } from "../../feature-module/application/components/chat/channelUtils";

export default function ChatMessageToast() {
    const { chatToastQueue, removeChatToast } = useChatNotificationContext();
    const navigate = useNavigate();

    // A push notification tapped while this tab is already open is routed
    // in-app by the service worker via postMessage instead of a page reload.
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type !== "OPEN_CONVERSATION") return;
            navigate(all_routes.chat, {
                state: {
                    openConversationId: event.data.endUserId,
                    chatTypeKey: event.data.chatTypeKey,
                    threadRootMessageId: event.data.threadRootMessageId,
                },
            });
        };

        navigator.serviceWorker.addEventListener("message", handleMessage);
        return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
    }, [navigate]);

    if (chatToastQueue.length === 0) return null;

    return (
        <>
            <style>{`
                @keyframes chatSlideInFromLeft {
                    from { transform: translateX(-110%); opacity: 0; }
                    to   { transform: translateX(0);     opacity: 1; }
                }
                @keyframes chatFloatUpDown {
                    0%,100% { transform: translateY(0);   }
                    50%     { transform: translateY(-6px); }
                }
            `}</style>

            <div
                style={{
                    position: "fixed",
                    bottom: "20px",
                    left: "20px",
                    zIndex: 10001,
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    maxWidth: "400px",
                    pointerEvents: "auto",
                }}
            >
                {chatToastQueue.slice(0, 3).map((toast) => {
                    const platformKey = (toast.platformKey || "").toUpperCase();
                    const key = (platformKey && platformKey !== "ENGATI" ? platformKey : toast.channelKey || "").toUpperCase();
                    const color = getChannelColor(key);
                    const shadow = getChannelColorAlpha(key, 0.4);
                    const icon = getChannelIcon(key);

                    return (
                        <div
                            key={toast.id}
                            style={{
                                background: "white",
                                borderRadius: "12px",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
                                padding: "20px",
                                minWidth: "380px",
                                animation: "chatSlideInFromLeft 0.4s ease-out, chatFloatUpDown 2s ease-in-out 0.4s infinite",
                                border: "1px solid rgba(0,0,0,0.08)",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "16px",
                                position: "relative",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.animationPlayState = "paused"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.animationPlayState = "running"; }}
                        >
                            <div
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "12px",
                                    background: (toast.platformKey === "GOOGLE_CHAT" || toast.platformKey === "LAZADA" || toast.platformKey === "TIKTOK") ? "white" : color,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginTop: "2px",
                                    flexShrink: 0,
                                    boxShadow: `0 4px 12px ${shadow}`,
                                    overflow: "hidden",
                                    padding: (toast.platformKey === "GOOGLE_CHAT" || toast.platformKey === "LAZADA" || toast.platformKey === "TIKTOK") ? "4px" : "0",
                                }}
                            >
                                {(toast.platformKey === "GOOGLE_CHAT" || toast.platformKey === "LAZADA" || toast.platformKey === "TIKTOK") ? (
                                    <img 
                                        src={`/assets/img/platform-icons/${toast.platformKey === "GOOGLE_CHAT" ? "Google-Chat" : toast.platformKey === "LAZADA" ? "Lazada" : "TikTok"}.png`}
                                        alt={toast.platformKey}
                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        onError={(e) => {
                                            const target = e.currentTarget;
                                            target.style.display = 'none';
                                            const parent = target.parentElement;
                                            if (parent) {
                                                parent.style.background = color;
                                                const fallback = document.createElement('i');
                                                fallback.className = icon;
                                                fallback.style.fontSize = "22px";
                                                fallback.style.color = "white";
                                                parent.appendChild(fallback);
                                            }
                                        }}
                                    />
                                ) : (
                                    <i className={icon} style={{ fontSize: "22px", color: "white" }} />
                                )}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p
                                    style={{
                                        margin: "0 0 2px",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        color,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    {toast.senderName}
                                </p>

                                <p
                                    style={{
                                        margin: "0 0 12px",
                                        fontSize: "14.5px",
                                        lineHeight: "1.5",
                                        color: "#2d3748",
                                        fontWeight: 500,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {toast.messagePreview}
                                </p>

                                <button
                                    onClick={() => {
                                        removeChatToast(toast.id);
                                        navigate(all_routes.chat, {
                                            state: { 
                                                openConversationId: toast.endUserId,
                                                chatTypeKey: toast.chatTypeKey,
                                                threadRootMessageId: toast.threadRootMessageId,
                                            },
                                        });
                                    }}
                                    style={{
                                        background: color,
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        padding: "5px 14px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        transition: "transform 0.2s, box-shadow 0.2s",
                                        boxShadow: `0 2px 8px ${shadow}`,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = `0 4px 12px ${shadow}`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = `0 2px 8px ${shadow}`;
                                    }}
                                >
                                    View Message
                                </button>
                            </div>

                            <button
                                onClick={() => removeChatToast(toast.id)}
                                style={{
                                    position: "absolute",
                                    top: "12px",
                                    right: "12px",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "2px",
                                    borderRadius: "6px",
                                    transition: "background 0.2s",
                                    color: "#a0aec0",
                                    fontSize: "16px",
                                    lineHeight: 1,
                                    width: "28px",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                                    e.currentTarget.style.color = "#2d3748";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = "#a0aec0";
                                }}
                            >
                                ×
                            </button>
                        </div>
                    );
                })}

                {chatToastQueue.length > 3 && (
                    <div
                        style={{
                            background: "linear-gradient(135deg, #824eeb 0%, #9d6ae8 100%)",
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(130,78,235,0.3), 0 2px 8px rgba(130,78,235,0.2)",
                            padding: "16px 20px",
                            minWidth: "380px",
                            animation: "chatSlideInFromLeft 0.4s ease-out",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "14px",
                        }}
                        onClick={() => {
                            navigate(all_routes.chat);
                            chatToastQueue.forEach((t) => removeChatToast(t.id));
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                        <span>+{chatToastQueue.length - 3} more messages</span>
                        <i className="ti ti-chevron-right" style={{ fontSize: "20px", opacity: 0.8 }} />
                    </div>
                )}
            </div>
        </>
    );
}
